// src/components/CoupleMatching.js
import React, { useState } from 'react';
import { formatExpiresAt } from '../utils/dateUtils';
import { apiFetch } from '../utils/apiFetch';
import '../styles/CoupleMatching.css';
import { useToast } from './Toast';

const CoupleMatching = ({ onClose, onSuccess, onInviteIssued }) => {
    const toast = useToast();
    const [activeTab, setActiveTab] = useState('create');
    const [inviteCode, setInviteCode] = useState('');
    const [inviteInfo, setInviteInfo] = useState(null);   // { inviteCode, expiresAt }
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleCreateInviteCode = async () => {
        setIsLoading(true);
        setError('');

        try {
            const response = await apiFetch('/members/create', {
                method: 'POST'
            });

            if (response.ok) {
                const result = await response.json();
                setInviteInfo(result.data);
                if (onInviteIssued) onInviteIssued(result.data);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.errorMessage || '초대 코드 발급 실패');
            }
        } catch (error) {
            console.error('초대 코드 발급 오류:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // 이미 커플에 소속된 경우(C004) — 낡은 캐시를 서버 정보로 복구
    const recoverCoupleInfo = async () => {
        const response = await apiFetch('/members/couple/info', {
            method: 'GET'
        });
        if (!response.ok) return false;
        const result = await response.json();
        // 매칭 대기(1인) 커플은 성공으로 처리하지 않음 — 자기 코드 입력으로 혼자 커플이 되는 것 방지
        if (!result.data.matched) {
            // 이 기기에 대기 캐시가 없어도 여기서 발견되면 대기 카드를 띄워 취소 경로를 열어준다
            if (onInviteIssued) {
                onInviteIssued({
                    inviteCode: result.data.inviteCode,
                    expiresAt: result.data.inviteExpiresAt
                });
            }
            setError('이미 발급한 초대 코드가 있어 합류할 수 없어요. 상대방에게 코드를 공유하고 합류를 기다려주세요. 다른 코드로 합류하려면 홈 화면에서 초대를 취소해주세요.');
            return true;
        }
        toast.success('이미 커플에 소속되어 있어 기존 커플 정보를 불러왔습니다.');
        if (onSuccess) onSuccess(result.data);
        onClose();
        return true;
    };

    const handleJoinCouple = async () => {
        if (!inviteCode.trim()) {
            setError('초대 코드를 입력해주세요.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await apiFetch('/members/join', {
                method: 'POST',
                body: JSON.stringify({ inviteCode: inviteCode.trim() })
            });

            if (response.ok) {
                const result = await response.json();
                toast.success('커플 매칭이 완료되었습니다!');
                if (onSuccess) {
                    onSuccess(result.data);
                }
                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                const errorData = await response.json();
                if (errorData.errorCode === 'C004' && await recoverCoupleInfo()) {
                    return;
                }
                throw new Error(errorData.errorMessage || '커플 매칭 실패');
            }
        } catch (error) {
            console.error('커플 매칭 오류:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClick = (e) => {
        if (e.target.classList.contains('couple-matching-overlay')) {
            onClose();
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('초대 코드가 클립보드에 복사되었습니다!');
        } catch (error) {
            console.error('클립보드 복사 실패:', error);
            toast.error('클립보드 복사에 실패했습니다.');
        }
    };

    return (
        <div className="couple-matching-overlay" onClick={handleModalClick}>
            <div className="couple-matching-modal">
                <div className="modal-header">
                    <h2>커플 매칭</h2>
                    <button onClick={onClose} className="modal-close-btn">✕</button>
                </div>

                <div className="modal-content">
                    <div className="tab-buttons">
                        <button
                            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
                            onClick={() => setActiveTab('create')}
                        >
                            초대 코드 생성
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'join' ? 'active' : ''}`}
                            onClick={() => setActiveTab('join')}
                        >
                            초대 코드 입력
                        </button>
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {activeTab === 'create' && (
                        <div className="create-section">
                            <p>초대 코드를 생성하고 상대방에게 공유하세요.</p>

                            <button
                                onClick={handleCreateInviteCode}
                                disabled={isLoading}
                                className="create-button"
                            >
                                {isLoading
                                    ? '발급 중...'
                                    : inviteInfo ? '새 코드 재발급' : '초대 코드 생성'}
                            </button>

                            {inviteInfo && (
                                <div className="generated-id">
                                    <h3>초대 코드</h3>
                                    <div className="id-display">
                                        <span className="couple-id">{inviteInfo.inviteCode}</span>
                                        <button
                                            onClick={() => copyToClipboard(inviteInfo.inviteCode)}
                                            className="copy-button"
                                        >
                                            복사
                                        </button>
                                    </div>
                                    <p className="help-text">{formatExpiresAt(inviteInfo.expiresAt)}</p>
                                    <p className="help-text">이 코드를 상대방에게 공유해주세요! 재발급하면 이전 코드는 사용할 수 없습니다.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'join' && (
                        <div className="join-section">
                            <p>상대방이 생성한 초대 코드를 입력하세요.</p>

                            <div className="input-section">
                                <input
                                    type="text"
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value.toUpperCase().trim())}
                                    maxLength={8}
                                    placeholder="8자리 초대 코드를 입력하세요"
                                    className="couple-id-input"
                                />
                                <button
                                    onClick={handleJoinCouple}
                                    disabled={isLoading}
                                    className="join-button"
                                >
                                    {isLoading ? '매칭 중...' : '커플 매칭'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoupleMatching;
